import React, { useState, useRef } from 'react';
import axios from 'axios';
import {
  Shield, ShieldAlert, ShieldCheck, Loader2, Mail, Link2, ArrowRight,
  CheckCircle2, XCircle, RefreshCw, Siren, Sparkles,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/scanner`;

const RISK_STYLES = {
  low: { ring: '#34d399', label: 'Low Risk', badge: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', Icon: ShieldCheck },
  medium: { ring: '#fbbf24', label: 'Medium Risk', badge: 'bg-amber-500/15 text-amber-400 border-amber-500/30', Icon: ShieldAlert },
  high: { ring: '#f87171', label: 'High Risk', badge: 'bg-red-500/15 text-red-400 border-red-500/30', Icon: Siren },
};

function ScoreRing({ score, risk }) {
  const style = RISK_STYLES[risk] || RISK_STYLES.medium;
  const circumference = 2 * Math.PI * 54;
  const offset = circumference * (1 - (score || 0) / 100);
  return (
    <div className="relative w-40 h-40 mx-auto" data-testid="scanner-score-ring">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10" />
        <circle
          cx="60" cy="60" r="54" fill="none" stroke={style.ring} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-extrabold text-white">{score}</span>
        <span className="text-xs text-gray-400">/ 100</span>
      </div>
    </div>
  );
}

const PublicScannerPage = () => {
  const [step, setStep] = useState('form'); // form -> otp -> results
  const [url, setUrl] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [scanId, setScanId] = useState(null);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [result, setResult] = useState(null);
  const [ctaSent, setCtaSent] = useState(false);
  const cooldownRef = useRef(null);

  const startCooldown = () => {
    setResendCooldown(45);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(cooldownRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleStartScan = async (e) => {
    e.preventDefault();
    if (!url.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/start`, { url: url.trim(), email: email.trim(), consent_marketing: consent });
      setScanId(res.data.scan_id);
      setStep('otp');
      startCooldown();
      toast({ title: 'Check your email', description: 'We just sent you a 6-digit verification code.' });
    } catch (error) {
      toast({ title: 'Could not start scan', description: error.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/verify`, { scan_id: scanId, code: code.trim() });
      if (res.data.scan_status !== 'ready') {
        // Prescan still finishing — poll status
        pollForResults();
      } else {
        setResult(res.data);
        setStep('results');
      }
    } catch (error) {
      toast({ title: 'Verification Failed', description: error.response?.data?.detail || 'Incorrect or expired code.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const pollForResults = () => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/status/${scanId}`);
        if (res.data.scan_status === 'ready' || res.data.scan_status === 'failed') {
          clearInterval(interval);
          setResult(res.data);
          setStep('results');
        }
      } catch (error) {
        clearInterval(interval);
      }
    }, 2500);
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await axios.post(`${API}/resend`, { scan_id: scanId });
      startCooldown();
      toast({ title: 'Code resent', description: 'Check your inbox for the new code.' });
    } catch (error) {
      toast({ title: 'Could not resend', description: error.response?.data?.detail || 'Please try again shortly.', variant: 'destructive' });
    }
  };

  const handleRequestDeepScan = async () => {
    try {
      const res = await axios.post(`${API}/request-deep-scan/${scanId}`);
      setCtaSent(true);
      toast({ title: 'Request received', description: res.data.message });
    } catch (error) {
      toast({ title: 'Error', description: 'Could not submit request.', variant: 'destructive' });
    }
  };

  const resetFlow = () => {
    setStep('form'); setUrl(''); setEmail(''); setConsent(false); setScanId(null);
    setCode(''); setResult(null); setCtaSent(false);
  };

  const riskStyle = result ? (RISK_STYLES[result.risk_level] || RISK_STYLES.medium) : null;

  return (
    <div className="min-h-screen bg-[#0b0f19] text-white" data-testid="public-scanner-page">
      <Header />

      <section className="pt-32 pb-20 max-w-3xl mx-auto px-4">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-red-500/30 bg-red-500/10 text-red-400 text-xs font-semibold mb-6">
            <Siren className="w-3.5 h-3.5" /> Joomla 911 — Free Site Health Check
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-4">
            Is your Joomla site quietly exposed?
          </h1>
          <p className="text-gray-400 text-base md:text-lg max-w-xl mx-auto">
            Run a free, no-login scan of your site's public surface — security headers, exposed backup files,
            and basic malware signatures — verified straight to your inbox.
          </p>
        </div>

        {step === 'form' && (
          <form
            onSubmit={handleStartScan}
            className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 space-y-5"
            data-testid="scanner-form"
          >
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block">Website URL</label>
              <div className="relative">
                <Link2 className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="yourwebsite.com"
                  className="pl-9 bg-white/5 border-white/15 text-white placeholder:text-gray-500"
                  data-testid="scanner-url-input"
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1.5 block">Your Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-9 bg-white/5 border-white/15 text-white placeholder:text-gray-500"
                  data-testid="scanner-email-input"
                  required
                />
              </div>
            </div>
            <label className="flex items-start gap-2.5 text-sm text-gray-400 cursor-pointer">
              <Checkbox checked={consent} onCheckedChange={setConsent} data-testid="scanner-consent-checkbox" className="mt-0.5" />
              I'd like to receive occasional Joomla security tips and product updates by email (optional).
            </label>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold rounded-full py-6 text-base"
              data-testid="scanner-start-button"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
              Scan My Site — Free
            </Button>
            <p className="text-xs text-gray-500 text-center">
              We'll email you a verification code before showing results. No spam, no card required.
            </p>
          </form>
        )}

        {step === 'otp' && (
          <form
            onSubmit={handleVerify}
            className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 space-y-5 text-center"
            data-testid="scanner-otp-form"
          >
            <Mail className="w-10 h-10 text-red-400 mx-auto" />
            <div>
              <h3 className="text-lg font-bold text-white">Check your inbox</h3>
              <p className="text-sm text-gray-400 mt-1">
                We sent a 6-digit code to <span className="text-white font-medium">{email}</span>
              </p>
            </div>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••"
              className="text-center text-2xl tracking-[0.5em] font-mono bg-white/5 border-white/15 text-white h-14"
              data-testid="scanner-otp-input"
              maxLength={6}
            />
            <Button
              type="submit"
              disabled={submitting || code.length !== 6}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold rounded-full py-6"
              data-testid="scanner-verify-button"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Verify & See My Report
            </Button>
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="text-sm text-gray-400 hover:text-white disabled:opacity-40 flex items-center gap-1.5 mx-auto"
              data-testid="scanner-resend-button"
            >
              <RefreshCw className="w-3.5 h-3.5" /> {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
            </button>
          </form>
        )}

        {step === 'results' && result && (
          <div className="space-y-6" data-testid="scanner-results">
            {result.scan_status === 'failed' ? (
              <div className="bg-white/5 border border-red-500/30 rounded-2xl p-8 text-center" data-testid="scanner-failed-message">
                <XCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
                <p className="text-white font-semibold">We couldn't reach that site.</p>
                <p className="text-gray-400 text-sm mt-1">{result.scan_error || 'Please check the URL and try again.'}</p>
              </div>
            ) : (
              <>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 text-center">
                  <ScoreRing score={result.exposure_score} risk={result.risk_level} />
                  <div className={`inline-flex items-center gap-1.5 mt-4 px-3 py-1 rounded-full border text-xs font-semibold ${riskStyle.badge}`} data-testid="scanner-risk-badge">
                    <riskStyle.Icon className="w-3.5 h-3.5" /> {riskStyle.label}
                  </div>
                  <p className="text-gray-400 text-sm mt-4 max-w-md mx-auto">{result.url}</p>
                </div>

                {result.ai_summary && (
                  <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-6" data-testid="scanner-ai-summary">
                    <div className="flex items-center gap-2 mb-2 text-red-400 text-sm font-semibold">
                      <Sparkles className="w-4 h-4" /> AI Summary
                    </div>
                    <p className="text-gray-200 text-sm leading-relaxed">{result.ai_summary}</p>
                    {result.ai_recommendation && (
                      <p className="text-gray-400 text-sm mt-3"><span className="text-white font-medium">Recommended next step: </span>{result.ai_recommendation}</p>
                    )}
                  </div>
                )}

                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8">
                  <h3 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wide">What we checked</h3>
                  <div className="space-y-3">
                    {result.headers_present?.map((h) => (
                      <div key={h} className="flex items-center gap-2 text-sm text-gray-300" data-testid={`scanner-check-pass-${h}`}>
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" /> {h} — present
                      </div>
                    ))}
                    {result.headers_missing?.map((h) => (
                      <div key={h} className="flex items-center gap-2 text-sm text-gray-300" data-testid={`scanner-check-fail-${h}`}>
                        <XCircle className="w-4 h-4 text-amber-400 flex-shrink-0" /> {h} — missing
                      </div>
                    ))}
                    <div className="flex items-center gap-2 text-sm text-gray-300" data-testid="scanner-check-version">
                      {result.joomla_version_disclosed ? (
                        <><XCircle className="w-4 h-4 text-amber-400 flex-shrink-0" /> Joomla version publicly disclosed ({result.joomla_version_disclosed})</>
                      ) : (
                        <><CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" /> No Joomla version disclosure detected</>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-300" data-testid="scanner-check-exposed-files">
                      {result.has_exposed_files ? (
                        <><XCircle className="w-4 h-4 text-red-400 flex-shrink-0" /> Possible exposed backup/config file detected</>
                      ) : (
                        <><CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" /> No exposed backup/config files found</>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-300" data-testid="scanner-check-malware">
                      {result.has_malware_pattern_hits ? (
                        <><XCircle className="w-4 h-4 text-red-400 flex-shrink-0" /> Possible malicious code pattern detected on homepage</>
                      ) : (
                        <><CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" /> No malware signatures found on homepage</>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-red-600/20 to-red-900/10 border border-red-500/30 rounded-2xl p-6 sm:p-8 text-center">
                  <Siren className="w-8 h-8 text-red-400 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-white mb-2">Want the full deep scan?</h3>
                  <p className="text-gray-300 text-sm max-w-md mx-auto mb-5">
                    This free check only looks at your public homepage. Our full Joomla 911 deep scan reviews
                    every file (and your database) inside an Akeeba backup, with AI-verified findings and
                    one-click cleanup.
                  </p>
                  {ctaSent ? (
                    <p className="text-emerald-400 text-sm font-medium" data-testid="scanner-cta-sent-message">
                      Thanks! Our team will follow up with next steps for your deep scan.
                    </p>
                  ) : (
                    <Button
                      onClick={handleRequestDeepScan}
                      className="bg-red-600 hover:bg-red-700 text-white font-bold rounded-full px-8 py-6"
                      data-testid="scanner-deep-scan-cta"
                    >
                      Get the Full Deep Scan <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </div>
              </>
            )}

            <button
              onClick={resetFlow}
              className="text-sm text-gray-500 hover:text-white mx-auto block"
              data-testid="scanner-reset-button"
            >
              Scan another site
            </button>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default PublicScannerPage;
